import LegalDisclaimerScreen from "@dana/Components/EZID/LegalDisclaimerScreen";
import OpeningScreen from "@dana/Components/EZID/OpeningScreen";
import SplLiteDisclaimerScreen from "@dana/Components/EZID/SplLiteDisclaimerScreen";
import Zone from "@insite/client-framework/Components/Zone";
import {
    ExcludeFromNavigation,
    ExcludeFromSignInRequired,
    HideFooter,
    HideFromSearchEngines,
    HideFromSiteSearch,
    HideHeader,
    HorizontalRule,
    LinkFieldValue,
    MetaDescription,
    MetaKeywords,
    OpenGraphImage,
    OpenGraphTitle,
    OpenGraphUrl,
    StructuredPageData,
} from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import * as React from "react";


const enum fields {
    // Opening Screen
    pageImage = "pageImage",
    pageImageAltText = "pageImageAltText",
    pageHeading = "pageHeading",
    patentPending = "patentPending",
    pageSubText = "pageSubText",
    pageDescription = "pageDescription",
    pageBottomText = "pageBottomText",
    enterPartNumberLabel = "enterPartNumberLabel",
    submitButtonLabel = "submitButtonLabel",
    watchVideoLabel = "watchVideoLabel",
    continueLabel = "continueLabel",
    stopLabel = "stopLabel",
    noPartNumberLabel = "noPartNumberLabel",
    clickHereLabel = "clickHereLabel",
    openingLeftImage = "openingLeftImage",
    openingLeftAltText = "openingLeftAltText",
    openingRightImage = "openingRightImage",
    openingRightAltText = "openingRightAltText",
    openingVideo = "openingVideo",
    openingVideoCaption = "openingVideoCaption",

    // SPL Lite Disclaimer
    splLiteDisclaimerHeading = "splLiteDisclaimerHeading",
    bulletinReferenceLink = "bulletinReferenceLink",
    bulletinReferenceText = "bulletinReferenceText",
    splLiteDisclaimerImage = "splLiteDisclaimerImage",
    splLiteDisclaimerImageAltText = "splLiteDisclaimerImageAltText",
    splLiteDisclaimerVideo = "splLiteDisclaimerVideo",
    splLiteDisclaimerVideoCaption = "splLiteDisclaimerVideoCaption",

    // Legal Disclaimer
    legalDisclaimerHeading = "legalDisclaimerHeading",
    legalDisclaimerText = "legalDisclaimerText",
    legalDisclaimerVideo = "legalDisclaimerVideo",
    legalDisclaimerVideoCaption = "legalDisclaimerVideoCaption",
    selectReadyShaftPage = "selectReadyShaftPage",

}

interface OwnProps extends PageProps {
    fields: {
        // Opening Screen
        [fields.pageImage]: string;
        [fields.pageImageAltText]: string;
        [fields.pageHeading]: string;
        [fields.patentPending]: string;
        [fields.pageSubText]: string;
        [fields.pageDescription]: string;
        [fields.pageBottomText]: string;
        [fields.enterPartNumberLabel]: string;
        [fields.submitButtonLabel]: string;
        [fields.watchVideoLabel]: string;
        [fields.continueLabel]: string;
        [fields.stopLabel]: string;
        [fields.noPartNumberLabel]: string;
        [fields.clickHereLabel]: string;
        [fields.openingLeftImage]: string;
        [fields.openingRightImage]: string;
        [fields.openingLeftAltText]: string;
        [fields.openingRightAltText]: string;
        [fields.openingVideo]: LinkFieldValue;
        [fields.openingVideoCaption]: LinkFieldValue;

        // SPL Lite Disclaimer
        [fields.splLiteDisclaimerHeading]: string;
        [fields.bulletinReferenceLink]: LinkFieldValue;
        [fields.bulletinReferenceText]: string;
        [fields.splLiteDisclaimerImage]: string;
        [fields.splLiteDisclaimerImageAltText]: string;
        [fields.splLiteDisclaimerVideo]: LinkFieldValue;
        [fields.splLiteDisclaimerVideoCaption]: LinkFieldValue;

        // Legal Disclaimer
        [fields.legalDisclaimerHeading]: string;
        [fields.legalDisclaimerText]: string;   
        [fields.legalDisclaimerVideo]: LinkFieldValue;
        [fields.legalDisclaimerVideoCaption]: LinkFieldValue;
        [fields.selectReadyShaftPage]: LinkFieldValue;
    };
}



const EZIDOpeningPage = ({ id, fields }: OwnProps) => {
    const [step, setStep] = React.useState(1);

    return (
        <Page>
            <Zone contentId={id} zoneName="Content" requireRows />    
            {step === 1 && (
                <OpeningScreen
                    fields={fields}
                    onProceedToNextScreen={() => setStep(2)}
                />
            )}
            {step === 2 && (
                <SplLiteDisclaimerScreen
                    fields={fields}
                    onProceedToNextScreen={() => setStep(3)}
                />
            )}
            {step === 3 && (
                <LegalDisclaimerScreen
                    fields={fields}
                />
            )}           
        </Page>
    )
};

const openningTab = {
    displayName: "Openning",
    sortOrder: 0,
};

const splLiteDisclaimerTab = {
    displayName: "SPL Lite Disclaimer",
    sortOrder: 1,
};

const legalDisclaimerTab = {
    displayName: "Legal Disclaimer",
    sortOrder: 2,
};

const pageModule: PageModule = {
    component: EZIDOpeningPage,
    definition: {
        displayName: "EZ-ID Opening Page",
        hasEditableTitle: true,
        hasEditableUrlSegment: true,
        pageType: "Content",
        fieldDefinitions: [
            MetaKeywords,
            MetaDescription,
            OpenGraphTitle,
            OpenGraphUrl,
            OpenGraphImage,
            StructuredPageData,
            {
                ...HorizontalRule,
                sortOrder: 199,
            },
            HideHeader,
            HideFooter,
            HideFromSearchEngines,
            HideFromSiteSearch,
            ExcludeFromNavigation,
            ExcludeFromSignInRequired,

            // Opening Screen
            {
                fieldType: "Translatable",
                name: fields.pageImage,
                editorTemplate: "ImagePickerField",
                displayName: "Page Image",
                defaultValue: "",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.pageImageAltText,
                editorTemplate: "TextField",
                displayName: "Page Image Alt Text",
                defaultValue: "",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.pageHeading,
                editorTemplate: "TextField",
                displayName: "Page Heading",
                defaultValue: "Dana EZ-ID",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.patentPending,
                editorTemplate: "TextField",
                displayName: "Patent Pending Label",
                defaultValue: "Patent Pending",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.pageSubText,
                editorTemplate: "TextField",
                displayName: "Page Sub Text",
                defaultValue: "No part number? No problem! Easily identify what to buy.",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.pageDescription,
                editorTemplate: "RichTextField",
                displayName: "Page Description",
                defaultValue:
                    "<p>Dana EZ-ID offers quick identification of driveline assembly part numbers that are missing product tags or other identifiers.</p>",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.pageBottomText,
                editorTemplate: "TextField",
                displayName: "Page Bottom Text",
                defaultValue:
                    "Honored with 2023 MEMA Aftermarket Suppliers Excellence Award",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.enterPartNumberLabel,
                editorTemplate: "TextField",
                displayName: "Enter Part Number Label",
                defaultValue: "Enter DriveShaft Part Number",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.submitButtonLabel,
                editorTemplate: "TextField",
                displayName: "Submit Button Label",
                defaultValue: "Submit",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.noPartNumberLabel,
                editorTemplate: "TextField",
                displayName: "No Part Number Label",
                defaultValue: "Don't have a Part Number?",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.clickHereLabel,
                editorTemplate: "TextField",
                displayName: "Click Here Label",
                defaultValue: "Click Here",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.watchVideoLabel,
                editorTemplate: "TextField",
                displayName: "Watch Video Label",
                defaultValue: "Watch The Video",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.continueLabel,
                editorTemplate: "TextField",
                displayName: "Continue Label",
                defaultValue: "Continue",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.stopLabel,
                editorTemplate: "TextField",
                displayName: "Stop Label",
                defaultValue: "Stop",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.openingLeftImage,
                editorTemplate: "ImagePickerField",
                displayName: "Opening Page's Left Image",
                defaultValue: "",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.openingLeftAltText,
                editorTemplate: "TextField",
                displayName: "Opening Page's Left Image Alt Text",
                defaultValue: "",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.openingRightImage,
                editorTemplate: "ImagePickerField",
                displayName: "Opening Page's Right Image",
                defaultValue: "",
                tab: openningTab,
            },
            {
                fieldType: "Translatable",
                name: fields.openingRightAltText,
                editorTemplate: "TextField",
                displayName: "Opening Page's Right Image Alt Text",
                defaultValue: "",
                tab: openningTab,
            },
            {
                name: fields.openingVideo,
                fieldType: "Translatable",
                displayName: "Opening Page's Video",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: openningTab,
            },
            {
                name: fields.openingVideoCaption,
                fieldType: "Translatable",
                displayName: "Opening Page's Video Caption",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: openningTab,
            },

            // SPL Lite Disclaimer
            {
                fieldType: "Translatable",
                name: fields.splLiteDisclaimerHeading,
                editorTemplate: "TextField",
                displayName: "SPL Lite Disclaimer Heading",
                defaultValue: "SPL Lite Disclaimer",
                tab: splLiteDisclaimerTab,
            },
            {
                name: fields.bulletinReferenceLink,
                fieldType: "Translatable",
                displayName: "Bulletin Reference Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: splLiteDisclaimerTab,
            },
            {
                fieldType: "Translatable",
                name: fields.bulletinReferenceText,
                editorTemplate: "TextField",
                displayName: "Bulletin Reference Text",
                defaultValue: "Spicer SPL Lite Technical Bulletin JOI-TEC-083",
                tab: splLiteDisclaimerTab,
            },
            {
                fieldType: "Translatable",
                name: fields.splLiteDisclaimerImage,
                editorTemplate: "ImagePickerField",
                displayName: "SPL Lite Disclaimer Image",
                defaultValue: "",
                tab: splLiteDisclaimerTab,
            },
            {
                fieldType: "Translatable",
                name: fields.splLiteDisclaimerImageAltText,
                editorTemplate: "TextField",
                displayName: "SPL Lite Disclaimer Image Alt Text",
                defaultValue: "",
                tab: splLiteDisclaimerTab,
            },
            {
                name: fields.splLiteDisclaimerVideo,
                fieldType: "Translatable",
                displayName: "SPL Lite Disclaimer Video",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: splLiteDisclaimerTab,
            },
            {
                name: fields.splLiteDisclaimerVideoCaption,
                fieldType: "Translatable",
                displayName: "SPL Lite Disclaimer Video Caption",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: splLiteDisclaimerTab,
            },

            // Legal Disclaimer
            {
                fieldType: "Translatable",
                name: fields.legalDisclaimerHeading,
                editorTemplate: "TextField",
                displayName: "Legal Disclaimer Heading",
                defaultValue: "Legal Disclaimer",
                tab: legalDisclaimerTab,
            },
            {
                fieldType: "Translatable",
                name: fields.legalDisclaimerText,
                editorTemplate: "RichTextField",
                displayName: "Legal Disclaimer Text",
                defaultValue:
                    "<h4>Purchaser is responsible for all measurements and parts selection</h4><p>No refunds or returns</p><p>All sales are final</p>",
                tab: legalDisclaimerTab,
            },
            {
                name: fields.legalDisclaimerVideo,
                fieldType: "Translatable",
                displayName: "Legal Disclaimer Video",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: legalDisclaimerTab,
            },
            
            {
                name: fields.legalDisclaimerVideoCaption,
                fieldType: "Translatable",
                displayName: "Legal Disclaimer Video Caption",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: legalDisclaimerTab,
            },

            {
                name: fields.selectReadyShaftPage,
                fieldType: "Translatable",
                displayName: "Select ReadyShaft Page",
                tooltip: "The 'Select ReadyShaft Page' to navigate to after the legal disclaimer.",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                tab: legalDisclaimerTab,
            },
        ],
    },
};

export default pageModule;
